import React from 'react';
import { ExpandableSection, ColumnLayout, SpaceBetween, Box, StatusIndicator } from '@amzn/awsui-components-console';
import { getCustomConfig } from './utils';
export const GROUP_VISIBLE_COLUMNS = ['name', 'id', 'ldap', 'deviceCount', 'config', 'privilege', 'thirdParty'];
export const GROUP_SORTED_FIELD = {
    id: 'privilege',
    header: 'Privilege',
    cell: (e) => e.privilege,
    sortingField: 'privilege',
    width: 150,
};
// Device table constants
const getStatusIndicator = (status) => {
    switch (status) {
        case 1:
            return <StatusIndicator type="success">Available</StatusIndicator>;
        case 3:
            return <StatusIndicator type="warning">In Use</StatusIndicator>;
        default:
            return <StatusIndicator type="stopped">Status {status}</StatusIndicator>;
    }
};

export const DEVICE_COLUMNS_DEFINITION = [
    {
        id: 'deviceName',
        header: 'Device name',
        cell: (item) => item.deviceName || item.releasedName,
        sortingField: 'deviceName',
        width: 200,
    },
    {
        id: 'serial',
        header: 'Serial',
        cell: (item) => item.serial,
        sortingField: 'serial',
        width: 150,
    },
    {
        id: 'owner',
        header: 'Group owner',
        cell: (item) => item.group?.owner?.name || item.owner?.name || item.group?.owner?.email || item.owner?.email || 'Unassigned',
        sortingField: 'owner',
        width: 200,
    },
    {
        id: 'groupName',
        header: 'Current group',
        cell: (item) => item.group?.name,
        sortingField: 'groupName',
        width: 200,
    },
];

export const DEVICE_VISIBLE_COLUMNS = ['deviceName', 'serial', 'owner', 'groupName', 'actions'];

export const DEVICE_SORTED_FIELD = {
    sortingColumn: DEVICE_COLUMNS_DEFINITION[0],
};

export const GROUP_COLUMNS_DEFINITION = [
    {
        id: 'name',
        header: 'Group name',
        cell: (e) => e.name,
        sortingField: 'name',
        width: 250,
    },
    {
        id: 'id',
        header: 'Group ID',
        cell: (e) => e.id,
        sortingField: 'id',
        width: 300,
    },
    {
        id: 'ldap',
        header: 'LDAP',
        cell: (e) => e.ldap,
        sortingField: 'ldap',
        width: 200,
    },
    GROUP_SORTED_FIELD,
    {
        id: 'deviceCount',
        header: 'Devices',
        cell: (e) => e.deviceCount || 0,
        sortingField: 'deviceCount',
        width: 120,
    },
    {
        id: 'thirdParty',
        header: 'Third party?',
        cell: (e) => e.thirdParty,
        sortingField: 'thirdParty',
        width: 150,
    },
    {
        id: 'config',
        header: 'Custom config',
        cell: (e) => getCustomConfig(e.config),
        width: 1000,
    },
];
export const VISIBLE_COLUMNS = ['name', 'id', 'ldap', 'config'];
// config key and items matches https://code.amazon.com/packages/DS2DeviceFarmGroupsManagementLambda/blobs/1b420abac93a153584664984138e43637082fdc9/--/src/ds2_device_farm_groups_management_lambda/services/configure_groups.py
export const GROUP_CONFIG_KEYS = {
    widgets: 'widgets',
    setup: 'setup',
    teardown: 'teardown',
};
export const NAVIGATION = 'navigation';
export const APPS = 'apps';
export const REMOTE_DEBUG = 'remote-debug';
export const ALEXA = 'alexa';
export const SHELL = 'shell';
export const APP_UPLOAD = 'app-upload';
export const SCREENSHOT = 'screenshot';
export const SCREEN_RECORDING = 'screen-recording';
export const LOGCAT = 'logcat';
export const ADVANCED_INPUT = 'advanced-input';
export const OTA_UPLOAD = 'ota-upload';
export const APP_LOGIN = 'app-login';
export const FLASH_BUILD = 'flash-build';
export const ADB_PUSH = 'adb-push';
export const MAINTENANCE = 'maintenance';
export const FILE_EXPLORER = 'file-explorer';
export const INFO = 'info';
export const WIDGETS = [
    NAVIGATION,
    APPS,
    REMOTE_DEBUG,
    ALEXA,
    SHELL,
    APP_UPLOAD,
    SCREENSHOT,
    SCREEN_RECORDING,
    LOGCAT,
    ADVANCED_INPUT,
    OTA_UPLOAD,
    APP_LOGIN,
    FLASH_BUILD,
    ADB_PUSH,
    MAINTENANCE,
    FILE_EXPLORER,
    INFO,
];
export const WIFI = 'wifi';
export const SETUP = [WIFI];
export const RETAIN_APPS = 'retain-apps';
export const FACTORY_RESET = 'factory-reset';
export const TEARDOWN = [RETAIN_APPS, FACTORY_RESET];
export const GROUP_CONFIG_DESCRIPTION = {
    widgets:
        'Check default to enable all the features. Otherwise, uncheck default to select which feature to be enabled from the dropdown list.',
    teardown:
        'Check default to disable all the steps in the device cleanup. Otherwise, uncheck default to select which step to be enabled from the dropdown list.',
    setup: 'Check default to disable all the steps in the device preparing. Otherwise, uncheck default to select which step to be enabled from the dropdown list.',
};
export const WIDGETS_OPTIONS = WIDGETS.map((item) => {
    return { label: item, value: item };
});
export const SETUP_OPTIONS = SETUP.map((item) => {
    return { label: item, value: item };
});
export const TEARDOWN_OPTIONS = [
    { label: RETAIN_APPS, value: RETAIN_APPS },
    { label: FACTORY_RESET, value: FACTORY_RESET, description: '1P only' },
];

export const COLLECTION_PREFERENCES_PROPS = {
    pageSizePreference: {
        title: 'Select page size',
        options: [
            { value: 20, label: '20 groups' },
            { value: 50, label: '50 groups' },
            { value: 100, label: '100 groups' },
        ],
    },
    visibleContentPreference: {
        title: 'Select visible columns',
        options: [
            {
                label: 'Main properties',
                options: GROUP_COLUMNS_DEFINITION.map(({ id, header }) => ({
                    id,
                    label: header,
                    editable: id !== 'name',
                })),
            },
        ],
    },
    cancelLabel: 'Cancel',
    confirmLabel: 'Confirm',
    title: 'Preferences',
    stickyColumnsPreference: {
        firstColumns: {
            title: 'Freeze first column(s)',
            description: 'Keep the first column(s) visible while horizontally scrolling the table content.',
            options: [
                { label: 'None', value: 0 },
                { label: 'First column', value: 1 },
            ],
        },
    },
};

export const DEVICE_COLLECTION_PREFERENCES_PROPS = {
    pageSizePreference: {
        title: 'Select page size',
        options: [
            { value: 10, label: '10 devices' },
            { value: 20, label: '20 devices' },
            { value: 50, label: '50 devices' },
        ],
    },
    visibleContentPreference: {
        title: 'Select visible columns',
        options: [
            {
                label: 'Device properties',
                options: DEVICE_COLUMNS_DEFINITION.map(({ id, header }) => ({
                    id,
                    label: header,
                    editable: id !== 'deviceName',
                })),
            },
        ],
    },
    cancelLabel: 'Cancel',
    confirmLabel: 'Confirm',
    title: 'Preferences',
    stickyColumnsPreference: {
        firstColumns: {
            title: 'Freeze first column(s)',
            description: 'Keep the first column(s) visible while horizontally scrolling the table content.',
            options: [
                { label: 'None', value: 0 },
                { label: 'First column', value: 1 },
            ],
        },
    },
};
